<template>
  <aside class="flex flex-col items-center h-full w-[72px] py-4 gap-2 overflow-y-auto bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700">
    
    <!-- Logo (con Fallback si falla la imagen) -->
    <router-link to="/gestion-reportes" class="flex-shrink-0 mb-1 relative group" title="Inicio">
      <img
        v-if="!logoError"
        :src="logoUrl"
        class="w-12 h-12 object-contain rounded-xl transition-transform duration-300 group-hover:scale-110"
        alt="Logo"
        @error="handleLogoError"
      />
      <!-- Si el logo falla, muestra las iniciales -->
      <div 
        v-else
        class="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-black shadow-md"
        :style="{ backgroundColor: avatarColor }"
      >
        {{ avatarInitials }}
      </div>
    </router-link>

    <div class="h-px w-10 bg-gray-200 dark:bg-gray-700 my-1"></div>

    <!-- Botón Bienvenida -->
    <router-link
      to="/bienvenida"
      class="w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-200 active:scale-90"
      :class="[
        $route.path === '/bienvenida'
          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 shadow-sm'
          : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
      ]"
      title="Bienvenida"
    >
      <Icon icon="mdi:home-heart" class="text-2xl" />
    </router-link>

    <div class="h-px w-10 bg-gray-200 dark:bg-gray-700 my-1"></div>

    <!-- Calculadora -->
    <button
      @click="toggleCalculadora"
      class="w-12 h-12 flex items-center justify-center rounded-xl text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 active:scale-90"
      title="Calculadora"
    >
      <Icon icon="mdi:calculator" class="text-2xl" />
    </button>

    <!-- Fullscreen -->
    <button
      @click="fullscreenToggle"
      class="w-12 h-12 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 active:scale-90"
      title="Pantalla completa"
    >
      <Icon :icon="fullscreenMode ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'" class="text-2xl" />
    </button>

    <!-- Dark mode -->
    <button
      @click="setTheme(!darkMode)"
      class="w-12 h-12 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 active:scale-90"
      :title="darkMode ? 'Modo claro' : 'Modo oscuro'"
    >
      <Icon :icon="darkMode ? 'ri:moon-fill' : 'ph:sun-dim'" class="text-2xl" />
    </button>

    <!-- Selector de Idioma / Traducción (ES, EN, ZH) -->
    <button
      @click="idiomaMenuToggle"
      class="relative w-12 h-12 flex items-center justify-center rounded-xl text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-all duration-200 active:scale-90"
      :class="[idiomaMenu ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : '']"
      :title="'Idioma / Language: ' + idiomaActualNombre"
    >
      <Icon icon="mdi:translate" class="text-2xl" />
      <span class="absolute -bottom-1 -right-0.5 text-[8.5px] font-black uppercase px-1 rounded bg-indigo-100 dark:bg-indigo-950/90 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 tracking-tighter">
        {{ idiomaActualCodigo.toUpperCase() }}
      </span>
    </button>

    <!-- Notificaciones (Icono del Sidebar) -->
    <button
      @click="notifToggle"
      class="relative w-12 h-12 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 active:scale-90"
      title="Notificaciones"
    >
      <span
        v-if="totalNotificaciones > 0"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white dark:border-gray-800 animate-pulseSoft"
      >
        {{ totalNotificaciones > 99 ? '99+' : totalNotificaciones }}
      </span>
      <Icon icon="mdi:bell-outline" class="text-2xl" />
    </button>

    <div class="flex-1"></div>

    <!-- Avatar (Abre el menú de perfil) -->
    <button
      @click="menuToggle"
      class="flex-shrink-0 rounded-full ring-2 ring-gray-200 dark:ring-gray-600 hover:ring-purple-400 transition-all duration-200 active:scale-95"
      title="Perfil"
    >
      <img
        v-if="userData.fotoPerfilUrl && !imageError"
        :src="userData.fotoPerfilUrl"
        class="w-10 h-10 rounded-full object-cover"
        alt="Foto"
        @error="handleImageError"
      />
      <div
        v-else
        class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
        :style="{ backgroundColor: avatarColor }"
      >
        {{ avatarInitials }}
      </div>
    </button>

    <!-- ============================================== -->
    <!-- TELEPORT: ESTO HACE QUE LOS MENÚS FLOTEN SOBRE TODO -->
    <!-- ============================================== -->
    <Teleport to="body">
      <!-- Overlay oscuro para cerrar en móvil -->
      <transition name="fade">
        <div
          v-if="menu || notification || idiomaMenu"
          class="fixed inset-0 bg-black/40 z-[9998]"
          @click="cerrarDropdowns"
        ></div>
      </transition>

      <!-- DROPDOWN IDIOMA / TRADUCTOR -->
      <transition name="slide-up">
        <div
          v-if="idiomaMenu"
          class="notranslate fixed z-[9999] w-72 border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700"
          style="top: 220px; left: 80px;"
          translate="no"
        >
          <div class="py-3 px-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40 flex items-center justify-between">
            <div class="flex items-center gap-2.5">
              <div class="p-1.5 bg-indigo-600 text-white rounded-lg shadow-sm">
                <Icon icon="mdi:translate" class="text-base" />
              </div>
              <div>
                <h3 class="text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider">
                  Idioma / Language
                </h3>
                <p class="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                  Predeterminado: Español
                </p>
              </div>
            </div>
            <button @click="idiomaMenu = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg">
              <Icon icon="mdi:close" class="text-base" />
            </button>
          </div>

          <div class="p-2 space-y-1">
            <button
              v-for="lang in idiomasDisponibles"
              :key="lang.codigo"
              @click="seleccionarIdioma(lang.codigo)"
              class="w-full flex items-center justify-between p-2.5 rounded-xl transition-all text-left"
              :class="[
                idiomaActualCodigo === lang.codigo
                  ? 'bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60'
              ]"
            >
              <div class="flex items-center gap-3">
                <span class="text-2xl flex-shrink-0">{{ lang.bandera }}</span>
                <div>
                  <div class="font-black text-xs flex items-center gap-1.5">
                    {{ lang.nombre }}
                    <span v-if="lang.predeterminado" class="text-[9px] font-bold px-1.5 py-0.2 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded uppercase">
                      Predeterminado
                    </span>
                  </div>
                  <div class="text-[11px] text-gray-400 dark:text-gray-400 font-medium">
                    {{ lang.nativo }}
                  </div>
                </div>
              </div>

              <div
                v-if="idiomaActualCodigo === lang.codigo"
                class="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-sm"
              >
                <Icon icon="mdi:check" class="text-xs font-bold" />
              </div>
            </button>
          </div>

          <div class="px-3.5 py-2 bg-gray-50 dark:bg-gray-800/80 text-[10px] text-gray-400 dark:text-gray-400 flex items-center gap-1.5">
            <Icon icon="mdi:information-outline" class="text-sm text-indigo-500 flex-shrink-0" />
            <span>Traducción instantánea de toda la plataforma</span>
          </div>
        </div>
      </transition>

      <!-- DROPDOWN PERFIL -->
      <transition name="slide-up">
        <div
          v-if="menu"
          class="fixed z-[9999] w-64 border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow-2xl divide-y dark:divide-gray-700 divide-gray-100 overflow-hidden"
          style="bottom: 80px; left: 16px;"
        >
          <div class="py-3 px-4 text-sm text-gray-900 dark:text-gray-200">
            <div class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Sesión iniciada como</div>
            <div class="font-bold truncate text-base">{{ userData.nombre }} {{ userData.apellido }}</div>
            <div class="text-xs text-gray-500 mt-1 truncate">{{ userData.email }}</div>
            <div class="text-[10px] font-black text-purple-600 dark:text-purple-400 mt-1 uppercase">{{ formatRole(userData.nombreRol) }}</div>
          </div>
          <ul class="py-1 text-sm text-gray-700 dark:text-gray-200">
            <li>
              <router-link
                to="/perfil"
                class="flex items-center py-3 px-4 hover:bg-purple-500 hover:text-white transition-colors"
                @click="menu = false"
              >
                <Icon icon="mdi:account" class="mr-3 text-lg" />
                Perfil de Usuario
              </router-link>
            </li>
          </ul>
          <div class="py-1">
            <a
              href="#"
              class="flex items-center py-3 px-4 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-500 hover:text-white transition-colors"
              @click.prevent="handleLogout"
            >
              <Icon icon="mdi:logout" class="mr-3 text-lg" />
              Cerrar Sesión
            </a>
          </div>
        </div>
      </transition>

      <!-- DROPDOWN NOTIFICACIONES -->
      <transition name="slide-up">
        <div
          v-if="notification"
          class="fixed z-[9999] w-[calc(100vw-32px)] max-w-sm border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col"
          style="bottom: 80px; left: 16px; max-height: 70vh;"
        >
          <div class="flex justify-between items-center p-3 border-b dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 rounded-t-xl">
            <h2 class="font-black text-sm text-gray-800 dark:text-gray-200 flex items-center gap-2">
              Notificaciones
              <span v-if="totalNotificaciones > 0" class="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{{ totalNotificaciones }}</span>
            </h2>
            <div class="flex gap-2">
              <button v-if="notifList.length > 0" @click="marcarTodasLeidas" class="text-blue-600 dark:text-blue-400 text-[10px] font-bold hover:underline">Marcar leídas</button>
              <button v-if="notifList.length > 0" @click="limpiarNotificaciones" class="text-red-500 hover:text-red-700 text-xs font-bold transition-colors">✕</button>
            </div>
          </div>

                    <!-- SECCIÓN DESTACADA: PAGOS POR VERIFICAR -->
          <div v-if="pagosPendientesLista.length > 0" class="p-3 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800/60">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-black text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                <Icon icon="mdi:credit-card-clock" class="text-amber-600 dark:text-amber-400 text-base" />
                Pagos por Verificar ({{ pagosPendientesLista.length }})
              </span>
              <button
                @click="verPagosPendientes"
                class="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline flex items-center gap-0.5"
              >
                Verificar <Icon icon="mdi:chevron-right" class="text-xs" />
              </button>
            </div>

            <div class="space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar">
              <div
                v-for="p in pagosPendientesLista.slice(0, 3)"
                :key="p.idPago"
                @click="verPagosPendientes"
                class="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 border border-amber-200 dark:border-amber-700/50 flex items-center justify-between cursor-pointer hover:bg-amber-100/50 dark:hover:bg-amber-900/40 transition-colors"
              >
                <div>
                  <div class="text-[11px] font-bold text-gray-800 dark:text-gray-100 flex items-center gap-1">
                    <span>Pago #{{ p.idPago }}</span>
                    <span class="text-[9px] text-amber-700 dark:text-amber-300 font-semibold">({{ p.metodo_pago || p.metodoPago || 'Pago Móvil' }})</span>
                  </div>
                  <div class="text-[9px] text-gray-500 dark:text-gray-400">
                    {{ p.codigoRecepcion ? ('Orden #' + p.codigoRecepcion) : (p.numeroOrden ? ('Orden #' + p.numeroOrden) : (p.referencia ? ('Ref: ' + p.referencia) : 'En línea')) }}
                  </div>
                </div>
                <span class="text-xs font-black text-emerald-600 dark:text-emerald-400">
                  ${{ (parseFloat(p.monto_usd || p.montoUsd || p.monto || 0)).toFixed(2) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="!notifList.length && pagosPendientesLista.length === 0" class="p-8 text-center flex-shrink-0">
            <div class="text-4xl mb-2 opacity-50">🔔</div>
            <p class="text-gray-500 dark:text-gray-400 text-xs font-bold">No hay notificaciones.</p>
          </div>

          <div class="overflow-y-auto flex-1 custom-scrollbar">
            <div
              v-for="(item, index) in notifList"
              :key="item.id || index"
              @click="irANotificacion(item)"
              class="p-3 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              :class="{ 'bg-blue-50/50 dark:bg-blue-900/20': !item.leida }"
            >
              <div class="flex gap-3 items-start">
                <div class="text-2xl flex-shrink-0">{{ item.icono || '🔔' }}</div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-start">
                    <div class="font-bold text-xs text-gray-800 dark:text-gray-200 truncate pr-2">{{ item.titulo }}</div>
                    <div class="flex items-center gap-1.5 flex-shrink-0">
                      <span v-if="!item.leida" class="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <button
                        @click.stop="borrarNotificacion(item.id)"
                        class="text-gray-400 hover:text-rose-500 p-0.5 rounded transition-colors"
                        title="Borrar"
                      >
                        <Icon icon="mdi:close" class="text-xs" />
                      </button>
                    </div>
                  </div>
                  <div class="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                    <span class="font-bold text-blue-600 dark:text-blue-400">{{ item.accion }}</span>
                    <span v-if="item.detalle" class="ml-1">· {{ limitText(item.detalle, 30) }}</span>
                  </div>
                  <div class="text-[9px] text-gray-400 dark:text-gray-500 mt-1 font-bold">{{ tiempoRelativo(item.fecha) }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-2 border-t dark:border-gray-700 flex-shrink-0">
            <button @click="verTodasNotificaciones" class="w-full py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
              Ver Todas
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </aside>
</template>

<script>
import { Icon } from "@iconify/vue";
import { fullscreen } from "@/helper/fullscreen";
import { setDarkMode, loadDarkMode } from "@/helper/theme";
import { apiFetch, getCurrentServer } from '@/api/index.js';
import { obtenerLogo } from '@/utils/tenantConfig';
import { IDIOMAS_DISPONIBLES, obtenerIdiomaActual, obtenerInfoIdioma, cambiarIdioma } from '@/utils/i18nService';
import {
  cargarNotificaciones, marcarTodasLeidas, marcarLeida,
  eliminarNotificacion, limpiarNotificaciones, contarNoLeidas, tiempoRelativo, verificarSuperAdmin
} from '@/utils/notificaciones';

export default {
  name: 'AppHeader',
  components: { Icon },
  props: {
    sidebarCollapsed: { type: Boolean, default: false }
  },
  data() {
    return {
      menu: false,
      darkMode: false,
      notification: false,
      idiomaMenu: false,
      idiomaActualCodigo: 'es',
      fullscreenMode: false,
      logoError: false,
      tenantConfigVersion: 0,
      userData: {
        idUsuario: null, nombre: '', apellido: '', email: '',
        nombreRol: '', fotoPerfilUrl: null,
      },
      notifList: [],
      notifNoLeidas: 0,
      pagosPendientesCount: 0,
      pagosPendientesLista: [],
      imageError: false,
    };
  },
  computed: {
    idiomasDisponibles() {
      return IDIOMAS_DISPONIBLES;
    },
    idiomaActualInfo() {
      return obtenerInfoIdioma(this.idiomaActualCodigo);
    },
    idiomaActualNombre() {
      return this.idiomaActualInfo?.nombre || 'Español';
    },
    totalNotificaciones() {
      return (this.notifNoLeidas || 0) + (this.pagosPendientesCount || 0);
    },
    logoUrl() {
      const _v = this.tenantConfigVersion;
      return obtenerLogo();
    },
    avatarInitials() {
      const n = this.userData.nombre || '';
      const a = this.userData.apellido || '';
      return ((n.charAt(0) + a.charAt(0)).toUpperCase()) || 'U';
    },
    avatarColor() {
      const colors = ['#4F46E5','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#6366F1'];
      let hash = 0;
      const str = this.avatarInitials;
      for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
      return colors[Math.abs(hash) % colors.length];
    }
  },
  methods: {
    fullscreen, setDarkMode, loadDarkMode, tiempoRelativo,
    verPagosPendientes() {
      this.notification = false;
      this.$router.push('/pagos');
    },
    menuToggle() {
      this.menu = !this.menu;
      if (this.menu) this.notification = false; // Cierra el otro menú
    },
    notifToggle() {
      this.notification = !this.notification;
      if (this.notification) {
        this.menu = false; // Cierra el otro menú
        this.actualizarNotificaciones();
      }
    },
    idiomaMenuToggle() {
      this.idiomaMenu = !this.idiomaMenu;
      if (this.idiomaMenu) {
        this.menu = false;
        this.notification = false;
      }
    },
    cerrarIdiomaMenu() {
      this.idiomaMenu = false;
    },
    seleccionarIdioma(codigo) {
      this.idiomaActualCodigo = codigo;
      this.idiomaMenu = false;
      cambiarIdioma(codigo);
    },
    cerrarDropdowns() {
      this.menu = false;
      this.notification = false;
      this.idiomaMenu = false;
    },
    limitText(msg, max = 25) {
      if (!msg) return '';
      return msg.length > max ? msg.substring(0, max) + '...' : msg;
    },
    actualizarNotificaciones() {
      if (!verificarSuperAdmin()) return;
      this.notifList = cargarNotificaciones();
      this.notifNoLeidas = contarNoLeidas();
    },
    marcarTodasLeidas() { marcarTodasLeidas(); this.actualizarNotificaciones(); },
    marcarLeida(id) { marcarLeida(id); this.actualizarNotificaciones(); },
    borrarNotificacion(id) { eliminarNotificacion(id); this.actualizarNotificaciones(); },
    limpiarNotificaciones() {
      if (confirm('¿Limpiar todas las notificaciones?')) {
        limpiarNotificaciones(); this.actualizarNotificaciones();
      }
    },
    verTodasNotificaciones() { this.notification = false; this.$router.push('/notificaciones'); },
    irANotificacion(item) {
      this.notification = false;
      this.marcarLeida(item.id);
      if (item.modulo === 'pago' || item.ruta === '/pagos') {
        this.$router.push('/pagos');
      } else {
        this.$router.push('/notificaciones');
      }
    },
    onNotificacionNueva() { this.actualizarNotificaciones(); },
    fullscreenToggle() {
      this.fullscreenMode = !this.fullscreenMode;
      this.fullscreen(this.fullscreenMode);
    },
    setTheme(bool) { this.darkMode = bool; this.setDarkMode(bool); },
    handleImageError() { this.imageError = true; this.userData.fotoPerfilUrl = null; },
    handleLogoError() {
      this.logoError = true;
      console.warn('⚠️ No se pudo cargar el logo desde tenantConfig');
    },
    refrescarTenantConfig() {
      this.tenantConfigVersion++;
      this.logoError = false;
    },
    async fetchUserImageUrl(usuarioId) {
      try {
        const imagenes = await apiFetch('/Imagen/listar/usuario');
        const img = imagenes.find(i => i.entidadId === usuarioId);
        return img ? img.url : null;
      } catch { return null; }
    },
    async loadUserData() {
      try {
        const s = localStorage.getItem('user') || localStorage.getItem('user_data');
        if (!s) return;
        const u = JSON.parse(s);
        const uid = u.idUsuario || u.id || 5;
        const url = await this.fetchUserImageUrl(uid);
        this.userData = {
          idUsuario: uid,
          nombre: u.nombre || '',
          apellido: u.apellido || '',
          email: u.email || '',
          nombreRol: u.nombreRol || u.rol?.nombre || 'Usuario',
          fotoPerfilUrl: url || null,
        };
        if (!this.userData.fotoPerfilUrl && u.fotoPerfil) {
          const server = getCurrentServer();
          this.userData.fotoPerfilUrl = `${server}/imagenes/usuario/${u.fotoPerfil}`;
        }
      } catch (e) { console.error(e); }
    },
    handleLogout() {
      if (confirm('¿Cerrar sesión?')) {
        ['user','user_data','token','userPermissions','isAuthenticated','rememberedUsername','sidebarCollapsed']
          .forEach(k => localStorage.removeItem(k));
        this.$router.push('/');
        this.$emit('userLoggedOut');
      }
    },
    formatRole(role) {
      if (!role) return 'Usuario';
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    toggleCalculadora() {
      this.$emit('open-calculadora');
    },
  },
  async mounted() {
    await this.loadUserData();
    this.darkMode = this.loadDarkMode();
    this.idiomaActualCodigo = obtenerIdiomaActual();
    this._onIdiomaCambiado = (e) => {
      this.idiomaActualCodigo = e.detail?.lang || 'es';
    };
    window.addEventListener('idiomaCambiado', this._onIdiomaCambiado);
    window.addEventListener('userUpdated', this.loadUserData);
    window.addEventListener('tenantConfigUpdated', this.refrescarTenantConfig);
    this.actualizarNotificaciones();
    this._onPagosPendientes = (e) => {
      this.pagosPendientesCount = e.detail?.cantidad || 0;
      this.pagosPendientesLista = e.detail?.pagos || [];
    };
    window.addEventListener('pagosPendientesActualizados', this._onPagosPendientes);
    this._notifInterval = setInterval(() => this.actualizarNotificaciones(), 30000);
  },
  beforeUnmount() {
    window.removeEventListener('userUpdated', this.loadUserData);
    window.removeEventListener('tenantConfigUpdated', this.refrescarTenantConfig);
    if (this._onPagosPendientes) window.removeEventListener('pagosPendientesActualizados', this._onPagosPendientes);
    if (this._notifInterval) clearInterval(this._notifInterval);
    if (this._onIdiomaCambiado) window.removeEventListener('idiomaCambiado', this._onIdiomaCambiado);
  }
};
</script>

<style scoped>
/* Animación para los menús */
.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-up-enter-from, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Scrollbar delgado para notificaciones */
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 9999px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }

@keyframes pulseSoft {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
}
.animate-pulseSoft {
  animation: pulseSoft 2s ease-in-out infinite;
}
</style>